import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconUndo } from "blocks-nextjs/blocks-react/components/Icons/Undo/Undo";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import React, { useState } from "react";
import useGlobalStore from "../../store/useGlobalStore";
import APIError from "../APIError/APIError";
import { Variant } from "../GlobalNotification/GlobalNotification";
import ChargerControlConfirmModal from "./ChargerControlConfirmModal";
import ConnectorCard from "./ConnectorCard";
const MINUTES_PER_HOUR = 60;
const CONNECTOR_1_INDEX = 0;
const CONNECTOR_2_INDEX = 1;
const CONNECTOR_1_NUMBER = 1;
const CONNECTOR_2_NUMBER = 2;
const CHARGER_STATUS_UNKNOWN = "UNKNOWN";
const CHARGER_STATUS_OFFLINE = "OFFLINE";
const REBOOT_MESSAGE_COLOR = "#3F3F3F";
const ChargerLiveView = ({ chargerDetails, connectorData, isError, isLoading, onRebootCharger, onRefresh, onStartCharger, onStopCharger, onViewChargeHistory, showChargerControls }) => {
    // Get connectors data
    const connectors = chargerDetails?.connectors || [];
    const connector1 = connectors[CONNECTOR_1_INDEX];
    const connector2 = connectors[CONNECTOR_2_INDEX];
    // Format duration to "X hr Y minutes"
    const formatDuration = (value, unit) => {
        if (value === undefined || value === null || !unit)
            return "N/A";
        if (unit === "minutes") {
            const hours = Math.floor(value / MINUTES_PER_HOUR);
            const minutes = value % MINUTES_PER_HOUR;
            if (hours > 0 && minutes > 0) {
                return `${hours} hr ${minutes} minutes`;
            }
            else if (hours > 0) {
                return `${hours} hr`;
            }
            else {
                return `${minutes} minutes`;
            }
        }
        return `${value} ${unit}`;
    };
    // Reboot button visibility: charger status is NOT Unknown or Offline
    const chargerStatus = chargerDetails?.lastKnownChargerStatus?.toUpperCase() || CHARGER_STATUS_UNKNOWN;
    const showRebootButton = showChargerControls &&
        !!onRebootCharger &&
        chargerStatus !== CHARGER_STATUS_UNKNOWN &&
        chargerStatus !== CHARGER_STATUS_OFFLINE;
    const [isRebootLoading, setIsRebootLoading] = useState(false);
    const [isRebootConfirmOpen, setIsRebootConfirmOpen] = useState(false);
    const setGlobalNotification = useGlobalStore((state) => state.setGlobalNotification);
    const handleRebootClick = () => {
        if (!onRebootCharger || isRebootLoading)
            return;
        setIsRebootConfirmOpen(true);
    };
    const handleRebootCancel = () => {
        if (isRebootLoading)
            return;
        setIsRebootConfirmOpen(false);
    };
    const handleRebootConfirm = async () => {
        if (!onRebootCharger || isRebootLoading)
            return;
        setIsRebootLoading(true);
        try {
            await onRebootCharger();
            setGlobalNotification({
                message: "Reboot command sent",
                showNotification: true,
                type: Variant.success
            });
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : typeof error === "string" ? error : "";
            setGlobalNotification({
                message: errorMessage ? `Failed to reboot charger: ${errorMessage}` : "Failed to reboot charger",
                showNotification: true,
                type: Variant.error
            });
        }
        finally {
            setIsRebootLoading(false);
            setIsRebootConfirmOpen(false);
        }
    };
    return (_jsxs("div", { "data-test": "charger-live-view", children: [_jsx(ChargerControlConfirmModal, { confirmLabel: "Reboot Charger", dataTest: "reboot-confirm-modal", heading: "Reboot Charger", isActive: isRebootConfirmOpen, isLoading: isRebootLoading, message: "Rebooting will reboot the charger and may cause a few minutes of downtime. Are you sure you want to reboot the charger?", messageColor: REBOOT_MESSAGE_COLOR, onCancel: handleRebootCancel, onConfirm: handleRebootConfirm }), _jsxs("div", { className: "flex justify-between items-center mb-4 mt-4", children: [_jsx(CatHeading, { "data-test": "live-view-title", variant: "title-sm", children: "Live View" }), showRebootButton && (_jsx(CatButton, { className: "rounded-[6px] h-9", "data-test": "reboot-charger-button", disabled: isRebootLoading, onClick: handleRebootClick, variant: "primary", children: isRebootLoading ? (_jsx(CatLoadingIndicator, {})) : (_jsxs("span", { className: "flex items-center gap-1", children: ["Reboot", _jsx("span", { className: "inline-flex [transform:scaleX(-1)]", children: _jsx(CatIconUndo, { slot: "after" }) })] })) }))] }), isError ? (_jsx(APIError, { refreshHandler: () => onRefresh?.() })) : (_jsxs("div", { className: "grid grid-cols-1 xl:grid-cols-2 gap-4", "data-test": "connectors-grid", children: [_jsx(ConnectorCard, { chargingMetrics: {
                            energyDelivered: connectorData?.connector1EnergyDelivered,
                            estimatedTimeTo80: connectorData?.connector1EstimatedTimeTo80,
                            estimatedTimeTo100: connectorData?.connector1EstimatedTimeTo100,
                            soc: connector1?.chargeTransaction?.latestSoc?.value
                        }, connector: connector1, connectorNumber: CONNECTOR_1_NUMBER, formatDuration: formatDuration, isLoading: isLoading, onStartCharger: onStartCharger, onStopCharger: onStopCharger, onViewChargeHistory: onViewChargeHistory, showChargerControls: showChargerControls }), _jsx(ConnectorCard, { chargingMetrics: {
                            energyDelivered: connectorData?.connector2EnergyDelivered,
                            estimatedTimeTo80: connectorData?.connector2EstimatedTimeTo80,
                            estimatedTimeTo100: connectorData?.connector2EstimatedTimeTo100,
                            soc: connector2?.chargeTransaction?.latestSoc?.value
                        }, connector: connector2, connectorNumber: CONNECTOR_2_NUMBER, formatDuration: formatDuration, isLoading: isLoading, onStartCharger: onStartCharger, onStopCharger: onStopCharger, onViewChargeHistory: onViewChargeHistory, showChargerControls: showChargerControls })] }))] }));
};
export default ChargerLiveView;
